import { RouterInputs, RouterOutputs } from '@crocodille/api';
import { isSome, Maybe } from '@crocodille/util';
import { useCallback, useEffect, useMemo } from 'react';
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation';
import useSWR from 'swr';
import { useApp } from '../app-provider';
import {
  AccountISICCardFormData,
  useAccountISICCardForm,
} from '../forms/account-isic-card-form';
import {
  LoyaltyCardFormData,
  useAccountLoyaltyCardForm,
} from '../forms/account-loyalty-card-form.v2';
import {
  AccountPasswordFormData,
  useAccountPasswordForm,
} from '../forms/account-password-form.v2';
import {
  RememberCardFormData,
  useAccountRememberCardForm,
} from '../forms/account-remember-card-form.v2';
import {
  AccountSettingsFormData,
  useAccountSettingsForm,
} from '../forms/account-settings-form.v2';
import { AppError, AppErrorType } from '../util/error';
import {
  AUTHORIZED_CUSTOMER_KEY,
  Customer,
  useAuthorizedCustomer,
} from './auth.v2';
import { useAccountBillingAddressForm } from '../forms/account-billing-address-form.v2';

export function useCustomerSettings(onSuccess?: () => void) {
  const { data: customer } = useAuthorizedCustomer();
  const { trpc } = useApp();

  const { trigger, isMutating, error } = useSWRMutation<
    Customer,
    AppErrorType<
      Extract<
        RouterOutputs['customer']['changeSettings'],
        { ok: false }
      >['error']
    >,
    typeof AUTHORIZED_CUSTOMER_KEY,
    AccountSettingsFormData
  >(
    AUTHORIZED_CUSTOMER_KEY,
    async (_, { arg: data }) => {
      const result = await trpc.customer.changeSettings.mutate(data);

      if (result.ok) {
        return result.data;
      }

      throw AppError.from(result.error);
    },
    {
      onSuccess: () => {
        onSuccess?.();
      },
    },
  );

  const form = useAccountSettingsForm(trigger, {
    initialData: {
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
    },
  });

  useEffect(() => {
    form.reset({
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
    });
  }, [customer]);

  return {
    form,
    isMutating,
    error,
  };
}

export function useCustomerPassword(onSuccess?: () => void) {
  const { trpc } = useApp();

  const { trigger, isMutating, error } = useSWRMutation<
    Customer,
    AppErrorType<
      Extract<
        RouterOutputs['customer']['changePassword'],
        { ok: false }
      >['error']
    >,
    typeof AUTHORIZED_CUSTOMER_KEY,
    AccountPasswordFormData
  >(
    AUTHORIZED_CUSTOMER_KEY,
    async (_, { arg: data }) => {
      const result = await trpc.customer.changePassword.mutate(data);

      if (result.ok) {
        return result.data;
      } else {
        throw AppError.from(result.error);
      }
    },
    {
      revalidate: false,
      throwOnError: false,
      onSuccess: () => {
        form.reset();
        onSuccess?.();
      },
    },
  );

  const form = useAccountPasswordForm(trigger);

  return {
    form,
    isMutating,
    error,
  };
}

export function useCustomerLoyaltyCard(onSuccess?: () => void) {
  const { trpc } = useApp();

  const { data: customer } = useAuthorizedCustomer();

  const { trigger, isMutating, error } = useSWRMutation<
    Customer,
    AppErrorType<
      Extract<
        RouterOutputs['customer']['changeLoyaltyCardV2'],
        { ok: false }
      >['error']
    >,
    typeof AUTHORIZED_CUSTOMER_KEY,
    LoyaltyCardFormData
  >(
    AUTHORIZED_CUSTOMER_KEY,
    async (_, { arg: data }) => {
      const result = await trpc.customer.changeLoyaltyCardV2.mutate(data);

      if (result.ok) {
        return result.data;
      } else {
        throw AppError.from(result.error);
      }
    },
    {
      throwOnError: false,
      onSuccess: () => {
        onSuccess?.();
      },
    },
  );

  const form = useAccountLoyaltyCardForm(trigger, {
    disabled: customer?.loyaltyCard?.physical,
    initialData: {
      loyaltyCardNumber: customer?.loyaltyCard?.cardNumber ?? '',
    },
  });

  const cardNumber = customer?.loyaltyCard?.cardNumber ?? '';

  useEffect(() => {
    form.reset({
      loyaltyCardNumber: cardNumber,
    });
  }, [cardNumber]);

  return {
    form,
    isMutating,
    error,
  };
}

export function useCustomerRememberCard() {
  const { trpc } = useApp();

  const { data: customer } = useAuthorizedCustomer();

  const { trigger, isMutating, error } = useSWRMutation<
    Customer,
    Error,
    typeof AUTHORIZED_CUSTOMER_KEY,
    RememberCardFormData
  >(AUTHORIZED_CUSTOMER_KEY, async (_, { arg: { rememberCard } }) => {
    const result = await trpc.customer.changeRememberCard.mutate({
      rememberCard: rememberCard ?? false,
    });

    return result;
  });

  const form = useAccountRememberCardForm(trigger, {
    initialData: {
      rememberCard: customer?.rememberCard ?? false,
    },
    disabled: !customer?.rememberCard,
  });

  useEffect(() => {
    form.reset({
      rememberCard: customer?.rememberCard ?? false,
    });
  }, [customer?.rememberCard]);

  return {
    form,
    isMutating,
    error,
  };
}

export type CustomerRegistration = RouterInputs['customer']['register'];

export function useCustomerRegistration(
  options?: SWRMutationConfiguration<
    void,
    AppError<{
      kind: Extract<
        RouterOutputs['customer']['register'],
        { ok: false }
      >['error'];
    }>,
    CustomerRegistration,
    'customer.register'
  >,
) {
  const { trpc } = useApp();

  const registerCustomer = useSWRMutation(
    'customer.register',
    async (
      _,
      {
        arg: registration,
      }: {
        arg: CustomerRegistration;
      },
    ) => {
      const result = await trpc.customer.register
        .mutate(registration)
        .catch((e) => {
          if (e.message.includes('Invalid email')) {
            throw AppError.from({ kind: 'email_invalid' });
          }
          throw AppError.from({ kind: 'other' });
        });

      if (result.ok) {
        return;
      } else {
        throw AppError.from({ kind: result.error });
      }
    },
    options,
  );

  return registerCustomer;
}

export function useCustomerReSendVerificationEmail(
  options?: SWRMutationConfiguration<
    void,
    Error,
    {
      readonly email: string;
    },
    'customer.requestVerify'
  >,
) {
  const { trpc } = useApp();

  const resendVerificationEmail = useSWRMutation(
    'customer.requestVerify',
    async (
      _,
      {
        arg: { email },
      }: {
        arg: {
          readonly email: string;
        };
      },
    ) => {
      return await trpc.customer.requestVerify.mutate({ email });
    },
    options,
  );

  return resendVerificationEmail;
}

export function useCustomerResetPasswordRequest(
  options?: SWRMutationConfiguration<
    void,
    Error,
    {
      readonly email: string;
    },
    'customer.requestPasswordReset'
  >,
) {
  const { trpc, locale } = useApp();

  const resetPassword = useSWRMutation(
    'customer.requestPasswordReset',
    async (
      _,
      {
        arg: { email },
      }: {
        arg: {
          readonly email: string;
        };
      },
    ) => {
      return await trpc.customer.requestPasswordReset.mutate({ email, locale });
    },
    options,
  );

  return resetPassword;
}

export function useCustomerResetPassword(
  passwordResetToken: string,
  options?: SWRMutationConfiguration<
    void,
    Error,
    {
      readonly newPassword: string;
      readonly confirmNewPassword: string;
    },
    'customer.resetPassword'
  >,
) {
  const { trpc } = useApp();

  const resetPassword = useSWRMutation(
    'customer.resetPassword',
    async (_, { arg: { newPassword, confirmNewPassword } }) => {
      return await trpc.customer.resetPassword.mutate({
        passwordResetToken,
        newPassword,
        confirmNewPassword,
      });
    },
    options,
  );

  return resetPassword;
}

export function useCustomerISICCard(onSuccess?: () => void) {
  const { trpc } = useApp();

  const { data: customer } = useAuthorizedCustomer();

  const { trigger, isMutating, error } = useSWRMutation<
    Customer,
    AppErrorType<
      Extract<
        RouterOutputs['customer']['changeISICCard'],
        { ok: false }
      >['error']
    >,
    typeof AUTHORIZED_CUSTOMER_KEY,
    AccountISICCardFormData
  >(
    AUTHORIZED_CUSTOMER_KEY,
    async (_, { arg: data }) => {
      const result = await trpc.customer.changeISICCard.mutate(data);

      if (!result.ok) {
        throw AppError.from(result.error);
      }

      return result.data;
    },
    {
      revalidate: false,
      populateCache: true,
      onSuccess: () => {
        onSuccess?.();
      },
    },
  );

  const initialData = useMemo(() => {
    return {
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      isicCardNumber: customer?.isicCard ?? '',
    };
  }, [customer]);

  const form = useAccountISICCardForm({
    initialData,
    onSubmit: trigger,
  });

  return {
    form,
    isMutating,
    error,
  };
}

const CUSTOMER_DELETE_ISIC_CARD_KEY = 'customer.deleteISICCard';

export function useDeleteCustomerISICCard(onSuccess?: () => void) {
  const { trpc } = useApp();

  const mutation = useSWRMutation(
    CUSTOMER_DELETE_ISIC_CARD_KEY,
    async () => {
      await trpc.customer.deleteISICCard.mutate(undefined);
    },
    {
      onSuccess: () => {
        onSuccess?.();
      },
    },
  );

  return mutation;
}

const ACCOUNT_DELETE_REQUEST_KEY = 'customer.deleteCustomer';

export function useCustomerAccountDeleteRequest(onSuccess?: () => void) {
  const { trpc } = useApp();

  const mutation = useSWRMutation(
    ACCOUNT_DELETE_REQUEST_KEY,
    async () => {
      await trpc.customer.deleteCustomer.mutate(undefined);
    },
    {
      onSuccess: () => {
        onSuccess?.();
      },
    },
  );

  return mutation;
}

const CUSTOMER_ACTIVITY_ADD_KEY = 'customer.addActivity';

export function useCustomerActivity(customerId: Maybe<number>) {
  const { trpc } = useApp();

  const { trigger, isMutating } = useSWRMutation(
    isSome(customerId) ? [CUSTOMER_ACTIVITY_ADD_KEY, customerId] : false,
    async (
      [_, customerId],
      {
        arg: { activity },
      }: {
        arg: {
          activity: RouterInputs['customer']['addActivity']['activity'];
        };
      },
    ) => {
      await trpc.customer.addActivity.mutate({
        customerId,
        activity,
      });
    },
  );

  const markAppLogin = useCallback(async () => {
    await trigger({
      activity: 'APP_LOGIN',
    });
  }, [trigger]);

  const markAppOpen = useCallback(async () => {
    await trigger({
      activity: 'APP_OPEN',
    });
  }, [trigger]);

  return {
    markAppLogin,
    markAppOpen,
    isMutating,
  };
}

const CUSTOMER_LOYALTY_CARD_PIN_KEY = 'customer.getLoyaltyCardPin';

export function useCustomerLoyaltyCardPin() {
  const { trpc } = useApp();

  return useSWR(CUSTOMER_LOYALTY_CARD_PIN_KEY, async () => {
    return await trpc.customer.getLoyaltyCardPin.query();
  });
}

const CUSTOMER_BILLING_ADDRESS_KEY = 'customer.billingAddress';

export function useCustomerBillingAddress() {
  const { trpc } = useApp();
  const { data: customer } = useAuthorizedCustomer();

  return useSWR(customer ? CUSTOMER_BILLING_ADDRESS_KEY : false, async () => {
    return await trpc.customer.billingAddress.query();
  });
}

export function useCustomerBillingAddressChange(onSuccess?: () => void) {
  const { trpc } = useApp();

  const { trigger, isMutating, error } = useSWRMutation(
    CUSTOMER_BILLING_ADDRESS_KEY,
    async (
      _,
      { arg: data }: { arg: RouterInputs['customer']['changeBillingAddress'] },
    ) => {
      await trpc.customer.changeBillingAddress.mutate(data);
    },
    {
      revalidate: true,
      onSuccess,
    },
  );

  const { data } = useCustomerBillingAddress();

  const form = useAccountBillingAddressForm(
    trigger,
    useMemo(
      () =>
        data
          ? {
              ...data,
              company: data.company ?? '',
              crn: data.crn ?? '',
              vat: data.vat ?? '',
            }
          : data,
      [data],
    ),
  );

  return {
    form,
    isMutating,
    error,
  };
}

export function useCustomerAppRating() {
  const { trpc } = useApp();

  const response = useSWRMutation(
    'customer.appRating',
    async (_, { arg }: { arg: RouterInputs['customer']['appRating'] }) => {
      await trpc.customer.appRating.mutate(arg);
    },
  );

  return response;
}

export function useCustomerFirstAppLogin() {
  const { trpc } = useApp();

  const response = useSWRMutation(
    AUTHORIZED_CUSTOMER_KEY,
    async () => {
      await trpc.customer.firstAppLogin.mutate();
    },
    {
      revalidate: true,
    },
  );

  return response;
}
